/**
 * Helper for route-loader dependencies that a page can render without.
 *
 * TanStack Router fails the whole route when its loader rejects, so one
 * optional section — a "latest articles" strip, a testimonial row — can take
 * the entire page down with it. `optionalLoaderData` degrades that section to
 * a fallback instead, so the page still renders.
 *
 * Only wrap data the page genuinely tolerates missing: the section must
 * already handle the fallback value (the homepage's `ArticlesSection` renders
 * nothing for an empty list). Core content — the thing the route exists to
 * show — should keep failing loudly.
 *
 * This does not hide the failure from Sentry. Server functions are wrapped by
 * `sentryFunctionMiddleware`, which reports the error server-side before it
 * ever reaches the loader. The `console.warn` here is a breadcrumb for the
 * client-side navigation case; it is deliberately `warn` rather than `error`
 * so it doesn't re-report through `captureConsoleIntegration` (which only
 * forwards `error`) and double-count an already-reported failure.
 *
 * Aliases: optional loader data, graceful loader degradation, non-critical
 * loader dependency.
 */
export function optionalLoaderData<T>(
  source: PromiseLike<T>,
  fallback: T,
  label: string,
): Promise<T> {
  return Promise.resolve(source).catch((error: unknown) => {
    console.warn(
      `[loader] optional data "${label}" failed; rendering without it`,
      error,
    );
    return fallback;
  });
}
